import React, { useState } from 'react';
import { Alert, Box, Button, Divider, FormControl, FormHelperText, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { gql, useMutation, useQuery } from 'urql';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import {
  CreateTeamMutation,
  CreateTeamMutationVariables,
  CreateTeamNameMappingMutation,
  CreateTeamNameMappingMutationVariables,
  FetchTeamsForManageTeamsQuery,
} from '../../generated/hooks';
import InputField from '../forms/InputField';
import SelectField from '../forms/SelectField';
import { TeamAutocomplete, TeamOption } from './TeamAutocomplete';

const fetchTeamsForManageTeams = gql`
  query fetchTeamsForManageTeams {
    teams {
      id
      name
      styles {
        primaryColor
        secondaryColor
        logoComponent
      }
    }
  }
`;

const createTeam = gql`
  mutation createTeam($input: CreateTeamInputType!) {
    createTeam(input: $input) {
      ok
      team {
        id
        name
      }
    }
  }
`;

const createTeamNameMapping = gql`
  mutation createTeamNameMapping($input: CreateTeamNameMappingInputType!) {
    createTeamNameMapping(input: $input) {
      ok
      mapping {
        id
        name
        source
        team {
          id
        }
      }
    }
  }
`;

const SOURCE_OPTIONS = [
  { label: 'AP Poll', value: 'AP_POLL' },
  { label: 'FB Schedules', value: 'FB_SCHEDULES' },
  { label: 'Sagarin', value: 'SAGARIN' },
  { label: 'The Score', value: 'THE_SCORE' },
];

type CreateTeamFormValues = { name: string; nickname: string; shortName: string; abbreviation: string };
type CreateMappingFormValues = { name: string; source: string; teamId: string };

function AdminManageTeams() {
  const { flashMutationError } = useFlashMessageContext();
  const [teamConfirmation, setTeamConfirmation] = useState<string>();
  const [mappingConfirmation, setMappingConfirmation] = useState<string>();

  const [{ data: teamsData, error: teamsError, fetching: teamsFetching }, reexecuteTeams] =
    useQuery<FetchTeamsForManageTeamsQuery>({ query: fetchTeamsForManageTeams });
  const teamOptions: TeamOption[] = [...(teamsData?.teams ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const teamsById = new Map(teamOptions.map(team => [team.id, team]));

  const {
    control: createTeamControl,
    handleSubmit: handleCreateTeamSubmit,
    reset: resetCreateTeamForm,
    formState: { isSubmitting: isCreatingTeam },
  } = useForm<CreateTeamFormValues>({ defaultValues: { name: '', nickname: '', shortName: '', abbreviation: '' } });

  const [, createTeamMutation] = useMutation<CreateTeamMutation, CreateTeamMutationVariables>(createTeam);

  const handleCreateTeam = handleCreateTeamSubmit(values => {
    return createTeamMutation({
      input: {
        name: values.name,
        nickname: values.nickname,
        shortName: values.shortName,
        abbreviation: values.abbreviation,
      },
    }).then(response => {
      if (flashMutationError(response.error)) {
        return;
      }
      setTeamConfirmation(`Added ${values.name}.`);
      resetCreateTeamForm();
      reexecuteTeams({ requestPolicy: 'network-only' });
    });
  });

  const {
    control: mappingControl,
    handleSubmit: handleMappingSubmit,
    reset: resetMappingForm,
    formState: { isSubmitting: isCreatingMapping },
  } = useForm<CreateMappingFormValues>({ defaultValues: { name: '', source: '', teamId: '' } });

  const [, createTeamNameMappingMutation] = useMutation<
    CreateTeamNameMappingMutation,
    CreateTeamNameMappingMutationVariables
  >(createTeamNameMapping);

  const handleCreateMapping = handleMappingSubmit(values => {
    return createTeamNameMappingMutation({
      input: { name: values.name, source: values.source, teamId: values.teamId },
    }).then(response => {
      if (flashMutationError(response.error)) {
        return;
      }
      setMappingConfirmation(
        `Mapped "${values.name}" to ${teamsById.get(values.teamId)?.name ?? 'the selected team'}.`,
      );
      resetMappingForm({ name: '', source: values.source, teamId: '' });
    });
  });

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Manage Teams
      </Typography>
      {teamsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load teams: {teamsError.message}
        </Alert>
      )}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Add Team
      </Typography>
      <form onSubmit={handleCreateTeam}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InputField
            control={createTeamControl}
            label="Name"
            name="name"
            placeholder="Tennessee"
            rules={{ required: 'Required' }}
          />
          <InputField
            control={createTeamControl}
            label="Nickname"
            name="nickname"
            placeholder="Volunteers"
            rules={{ required: 'Required' }}
          />
          <InputField
            control={createTeamControl}
            label="Short Name"
            name="shortName"
            placeholder="tennessee"
            rules={{ required: 'Required' }}
          />
          <InputField
            control={createTeamControl}
            label="Abbreviation"
            name="abbreviation"
            placeholder="TENN"
            rules={{ required: 'Required' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" type="submit" disabled={isCreatingTeam}>
              {isCreatingTeam ? 'Saving…' : 'Add Team'}
            </Button>
            {teamConfirmation && (
              <Typography variant="body2" color="success.main">
                {teamConfirmation}
              </Typography>
            )}
          </Box>
        </Box>
      </form>
      <Divider sx={{ my: 4 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>
        Add Team Name Mapping
      </Typography>
      <form onSubmit={handleCreateMapping}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InputField control={mappingControl} label="Name" name="name" rules={{ required: 'Required' }} />
          <SelectField
            control={mappingControl}
            label="Source"
            name="source"
            options={SOURCE_OPTIONS}
            rules={{ required: 'Required' }}
            sx={{ width: '100%' }}
          />
          <Controller
            control={mappingControl}
            name="teamId"
            rules={{ required: 'Required' }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormControl error={!!error}>
                <TeamAutocomplete
                  options={teamOptions}
                  value={teamsById.get(value) ?? null}
                  onChange={team => onChange(team?.id ?? '')}
                />
                {error && (
                  <FormHelperText>
                    <Typography variant="caption" color="error">
                      {error.message ?? 'Chaloux'}
                    </Typography>
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" type="submit" disabled={isCreatingMapping || teamsFetching}>
              {isCreatingMapping ? 'Saving…' : 'Add Mapping'}
            </Button>
            {mappingConfirmation && (
              <Typography variant="body2" color="success.main">
                {mappingConfirmation}
              </Typography>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  );
}

export default AdminManageTeams;
